import { Link } from "gatsby"
import { FaArrowRight } from "react-icons/fa6"

type ButtonProps = {
  children: React.ReactNode
  className?: string
  variant?: "primary" | "secondary" | "tertiary" | "white"
  to?: string
  hideIcon?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>

export const Button = ({
  children,
  className = "",
  variant = "primary",
  to,
  hideIcon = false,
  disabled,
  ...props
}: ButtonProps) => {
  const styles = {
    primary: "bg-primary-dark text-white hover:bg-primary-dark/90 transition",
    secondary:
      "text-primary-dark  border border-primary-dark hover:bg-primary-dark/20",
    tertiary: "text-primary-soft hover:text-primary-soft/80 font-medium",
    white:
      "bg-transparent border border-white/40 text-white hover:bg-white/10 transition",
  }

  const classes = `${
    disabled ? "opacity-50 cursor-not-allowed" : ""
  } relative overflow-hidden font-medium px-[30px] py-[16px] w-fit rounded-full text-sm md:text-md flex items-center group duration-300 ease-in-out ${
    styles[variant]
  } ${className}`

  //shimmer effect on hover
  const shimmer = (
    <span className="pointer-events-none absolute inset-0 rounded-full overflow-hidden">
      <span className="absolute -left-[60%] top-0 rounded-full opacity-60 h-full w-[50%] bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:left-[120%] transition-all duration-700 ease-out"></span>
    </span>
  )

  if (to) {
    return (
      <Link
        aria-label={props["aria-label"] || "Button"}
        to={to}
        className={classes}
      >
        {variant === "primary" && shimmer}

        <span className="relative flex items-center">
          {children}

          {variant !== "secondary" && !hideIcon && (
            <span className="ml-2 group-hover:translate-x-[4px] transition duration-300 ease-in-out">
              <FaArrowRight size={14} />
            </span>
          )}
        </span>
      </Link>
    )
  }

  return (
    <button
      aria-label={props["aria-label"] || "Button"}
      className={classes}
      disabled={disabled}
      {...props}
    >
      {variant === "primary" && !disabled && shimmer}
      <span className="relative flex items-center">
        {children}

        {variant !== "secondary" && !hideIcon && (
          <span className="ml-2 group-hover:translate-x-[4px] transition duration-300 ease-in-out">
            <FaArrowRight size={14} />
          </span>
        )}
      </span>
    </button>
  )
}
